import React, { useState } from "react";
import { X, Save, Loader2, User, Headphones } from "lucide-react";

const OperatorSetupModal = ({ isOpen, onSave, isSaving = false }) => {
  const [operatorName, setOperatorName] = useState("");
  const [error, setError] = useState("");

  const validateOperatorName = (name) => {
    if (!name.trim()) {
      return "Operator name is required";
    }
    
    // Check for symbols - only allow letters, numbers, and spaces
    const symbolRegex = /^[a-zA-Z0-9\s]+$/;
    if (!symbolRegex.test(name.trim())) {
      return "Operator name can only contain letters, numbers, and spaces";
    }
    
    return null;
  };

  const handleInputChange = (value) => {
    setOperatorName(value);
    if (error) {
      const validationError = validateOperatorName(value);
      setError(validationError || "");
    }
  };

  const handleSave = async () => {
    const validationError = validateOperatorName(operatorName);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      // Save to localStorage
      localStorage.setItem('operatorName', operatorName.trim());
      
      // Call parent callback
      if (onSave) {
        await onSave(operatorName.trim());
      }
    } catch (err) {
      setError("Failed to save operator name");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay - non-dismissible */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"></div>

        {/* Modal content */}
        <div
          className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl"
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Headphones className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Setup Operator
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enter your name for conversation assignments
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Operator Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Operator Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={operatorName}
                  onChange={(e) => handleInputChange(e.target.value)}
                  disabled={isSaving}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    error
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  placeholder="Enter your operator name"
                  autoFocus
                />
              </div>
              {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This name will be used when you take over conversations
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-6 mt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Press{" "}
              <kbd className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                Enter
              </kbd>{" "}
              to save
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={isSaving || !operatorName.trim()}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorSetupModal;
